import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsAndConditions = () => {
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  const sections = [
    {
      id: 1,
      title: "1. Definitions",
      content: (
        <>
          <p><strong>"Service"</strong> refers to the Figbot AI chatbot platform, dashboard, scripts, APIs, and related tools.</p>
          <p><strong>"User", "You", "Your"</strong> refers to the individual or organization using Figbot.</p>
          <p><strong>"Website"</strong> refers to the website(s) where you embed the Figbot widget.</p>
          <p><strong>"Content"</strong> refers to any text, data, or information provided or uploaded by you.</p>
        </>
      )
    },
    {
      id: 2,
      title: "2. Eligibility",
      content: (
        <>
          <p>By using Figbot, you confirm that:</p>
          <ul>
            <li>You are at least 18 years old.</li>
            <li>You have the authority to bind yourself or your organization to these Terms.</li>
          </ul>
        </>
      )
    },
    {
      id: 3,
      title: "3. Description of Service",
      content: (
        <>
          <p>Figbot is an AI-powered chatbot that:</p>
          <ul>
            <li>Reads and analyzes website content</li>
            <li>Provides automated responses to visitors</li>
            <li>Allows customization through an Admin Panel</li>
            <li>Offers analytics, logs, and performance insights</li>
          </ul>
          <p>We reserve the right to modify, update, or discontinue certain features at any time.</p>
        </>
      )
    },
    {
      id: 4,
      title: "4. Account Registration",
      content: (
        <ul>
          <li>You must create an account to access the dashboard.</li>
          <li>You must provide accurate information and keep it updated.</li>
          <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
          <li>Any activity under your account is your responsibility.</li>
        </ul>
      )
    },
    {
      id: 5,
      title: "5. User Responsibilities",
      content: (
        <>
          <p><strong>You agree NOT to:</strong></p>
          <ul>
            <li>Use Figbot for illegal, harmful, or abusive purposes</li>
            <li>Embed Figbot on websites containing prohibited content (violence, hate speech, adult content, malicious software, etc.)</li>
            <li>Attempt to reverse engineer, modify, or copy Figbot's code</li>
            <li>Exceed usage limits defined in your plan</li>
          </ul>
          <p><strong>You are responsible for:</strong></p>
          <ul>
            <li>Ensuring your website content complies with applicable laws</li>
            <li>Ensuring the chatbot responses are used appropriately</li>
            <li>Any data processed through your Figbot integration</li>
          </ul>
        </>
      )
    },
    {
      id: 6,
      title: "6. Intellectual Property Rights",
      content: (
        <ul>
          <li>Figbot owns all intellectual property rights to its platform, branding, UI, algorithms, and code.</li>
          <li>You retain ownership of your website content used to train the chatbot.</li>
          <li>You grant us permission to analyze, index, and process your website content to improve the Service.</li>
        </ul>
      )
    },
    {
      id: 7,
      title: "7. Payment, Billing & Refunds",
      content: (
        <>
          <div style={styles.subsection}>
            <h3 style={styles.subsectionTitle}>7.1 Billing</h3>
            <ul>
              <li>Plans are billed monthly or annually based on the selected subscription.</li>
              <li>All prices are listed on the website and may be updated with notice.</li>
            </ul>
          </div>
          <div style={styles.subsection}>
            <h3 style={styles.subsectionTitle}>7.2 Auto-Renewal</h3>
            <p>Subscriptions renew automatically unless cancelled.</p>
          </div>
          <div>
            <h3 style={styles.subsectionTitle}>7.3 Refund Policy</h3>
            <ul>
              <li>Figbot does not offer refunds for monthly plans.</li>
              <li>Annual plan refunds may be considered only if requested within 7 days of purchase.</li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 8,
      title: "8. Usage Limits",
      content: (
        <>
          <p>Depending on your plan, limits may apply to:</p>
          <ul>
            <li>Number of websites</li>
            <li>Number of messages per month</li>
            <li>Number of team members</li>
            <li>API usage</li>
          </ul>
          <p>If your usage exceeds limits, you may be required to upgrade.</p>
        </>
      )
    },
    {
      id: 9,
      title: "9. Data Privacy",
      content: (
        <ul>
          <li>Figbot does not sell or share your data with third parties.</li>
          <li>All chatbot data is processed securely using industry-standard encryption.</li>
          <li>Users must review and comply with their local data protection laws (GDPR, etc.).</li>
          <li>You agree to our Privacy Policy, which explains how data is stored and processed.</li>
        </ul>
      )
    },
    {
      id: 10,
      title: "10. Service Availability & Uptime",
      content: (
        <>
          <p>We strive to maintain 99% uptime, but:</p>
          <ul>
            <li>We do not guarantee uninterrupted access.</li>
            <li>Scheduled maintenance may occur.</li>
            <li>We are not liable for downtime caused by third-party providers, APIs, or hosting issues.</li>
          </ul>
        </>
      )
    },
    {
      id: 11,
      title: "11. Limitation of Liability",
      content: (
        <>
          <p>To the fullest extent permitted by law:</p>
          <ul>
            <li>Figbot is not liable for indirect, incidental, or consequential damages.</li>
            <li>Figbot is not responsible for inaccurate chatbot responses derived from your website content.</li>
            <li>Your use of Figbot is at your own risk.</li>
            <li>Our total liability shall not exceed the amount paid by you in the last 12 months.</li>
          </ul>
        </>
      )
    },
    {
      id: 12,
      title: "12. Termination",
      content: (
        <>
          <p><strong>We may suspend or terminate your account if:</strong></p>
          <ul>
            <li>You violate these Terms</li>
            <li>You engage in fraudulent or harmful behavior</li>
            <li>You fail to pay subscription fees</li>
          </ul>
          <p><strong>Upon termination:</strong></p>
          <ul>
            <li>Your bot will stop working</li>
            <li>Access to dashboard may be removed</li>
            <li>No refunds will be issued</li>
          </ul>
          <p>You may terminate your account anytime through the dashboard.</p>
        </>
      )
    },
    {
      id: 13,
      title: "13. Third-Party Services",
      content: (
        <>
          <p>Figbot may integrate with third-party platforms (WordPress, Shopify, etc.).</p>
          <p><strong>We are not responsible for:</strong></p>
          <ul>
            <li>Their policies</li>
            <li>Their downtime</li>
            <li>Their data handling</li>
          </ul>
        </>
      )
    },
    {
      id: 14,
      title: "14. Modifications to Terms",
      content: (
        <p>We may update these Terms periodically. Continued use of Figbot after updates constitutes acceptance of the new Terms.</p>
      )
    },
    {
      id: 15,
      title: "15. Governing Law",
      content: (
        <>
          <p>These Terms are governed by the laws of [Insert Country/State Jurisdiction].</p>
          <p>Any disputes will be resolved through binding arbitration or competent courts in that jurisdiction.</p>
        </>
      )
    }
  ];

  const toggleSection = (id) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSections(newExpanded);
  };

  const toggleAllSections = () => {
    if (allExpanded) {
      setExpandedSections(new Set());
    } else {
      setExpandedSections(new Set(sections.map(s => s.id)));
    }
    setAllExpanded(!allExpanded);
  };


  return (
    <>
        <Header />
        <div style={styles.body}>
      <div style={styles.container}>
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.6s ease-out;
          }
        `}</style>
    
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>Terms & Conditions</h1>
          <p style={styles.headerSubtitle}>Figbot AI Chatbot Platform</p>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Last Updated */}
          <div style={styles.lastUpdatedBox}>
            <strong>Last Updated:</strong> January 2025
          </div>

          {/* Introduction */}
          <p style={styles.intro}>
            Welcome to Figbot ("we", "our", "us"). These Terms & Conditions govern your use of our website, services, and AI chatbot solution. By using Figbot, you agree to these Terms. If you do not agree, please stop using the Service.
          </p>

          {/* Expand All Button */}
          <div style={styles.buttonContainer}>
            <button
              onClick={toggleAllSections}
              style={styles.expandButton}
            >
              {allExpanded ? 'Collapse All Sections' : 'Expand All Sections'}
            </button>
          </div>

          {/* Sections */}
          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            return (
              <div
                key={section.id}
                style={{
                  ...styles.sectionBox,
                  ...(isExpanded ? styles.sectionBoxExpanded : {})
                }}
                onClick={() => toggleSection(section.id)}
              >
                <h2 style={styles.sectionTitle}>
                  {section.title}
                  <span style={{
                    ...styles.arrow,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ▼
                  </span>
                </h2>
                <div
                  style={{
                    ...styles.sectionContent,
                    maxHeight: isExpanded ? '2000px' : '0',
                    opacity: isExpanded ? 1 : 0
                  }}
                >
                  <div style={styles.sectionText}>
                    {section.content}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Contact Box */}
          <div style={styles.contactBox}>
            <h2 style={styles.contactTitle}>16. Contact Information</h2>
            <p style={styles.contactText}>For any concerns or questions:</p>
            <p style={styles.contactText}>
              <strong>Email:</strong>{' '}
              <a href="mailto:support@figbot.ai" style={styles.contactLink}>
                support@figbot.ai
              </a>
            </p>
            <p style={styles.contactText}>
              <strong>Website:</strong>{' '}
              <a href="https://www.figbot.ai" target="_blank" rel="noopener noreferrer" style={styles.contactLink}>
                www.figbot.ai
              </a>
            </p>
            <p style={styles.contactText}>
              <strong>Address:</strong> [Insert Company Address]
            </p>
          </div>
        </div>
      </div>
        </div>
       <Footer />
    </>
  
  );
};

const styles = {
  body: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    animation: 'fadeIn 0.6s ease-out',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '40px 20px',
    textAlign: 'center',
  },
  headerTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: '10px',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: '1.1rem',
    opacity: 0.95,
    margin: 0,
  },
  content: {
    padding: '40px',
  },
  lastUpdatedBox: {
    background: '#f3f4f6',
    padding: '15px',
    borderLeft: '4px solid #667eea',
    borderRadius: '8px',
    marginBottom: '30px',
  },
  intro: {
    marginBottom: '30px',
    fontSize: '1.1rem',
    color: '#555',
    lineHeight: 1.7,
  },
  buttonContainer: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  expandButton: {
    background: '#667eea',
    color: 'white',
    padding: '12px 32px',
    borderRadius: '50px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
  },
  sectionBox: {
    marginBottom: '20px',
    padding: '24px',
    background: '#f3f4f6',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  sectionBoxExpanded: {
    background: '#e0e7ff',
    borderLeft: '4px solid #667eea',
  },
  sectionTitle: {
    color: '#667eea',
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0 0 15px 0',
  },
  arrow: {
    fontSize: '0.8rem',
    transition: 'transform 0.3s',
  },
  sectionContent: {
    overflow: 'hidden',
    transition: 'max-height 0.3s, opacity 0.3s',
  },
  sectionText: {
    color: '#555',
    lineHeight: 1.7,
  },
  subsection: {
    marginBottom: '20px',
  },
  subsectionTitle: {
    color: '#374151',
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: '10px',
    marginTop: '15px',
  },
  contactBox: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '30px',
    borderRadius: '12px',
    marginTop: '40px',
    textAlign: 'center',
  },
  contactTitle: {
    fontSize: '1.8rem',
    fontWeight: 700,
    marginBottom: '20px',
    margin: '0 0 20px 0',
  },
  contactText: {
    marginBottom: '10px',
    fontSize: '1.1rem',
    margin: '0 0 10px 0',
  },
  contactLink: {
    color: 'white',
    textDecoration: 'none',
    borderBottom: '2px solid rgba(255,255,255,0.5)',
    transition: 'border-color 0.3s',
  },
};

export default TermsAndConditions;

